import { useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { Button, Flex, FormControl, Input, Text } from '@eqi-investimentos/react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useSendOTP } from '@/modules/Customer/http/client/mutations/useSendOTP';
import { useUpdatePassword } from '@/modules/Customer/http/client/mutations/useUpdatePassword';
import { useGetAccount } from '@/modules/Customer/http/client/useGetAccount';
import { Drawer } from '@/components/molecules/Drawer';
import { EDIT_PROFILE_INFO_DRAWER_IDS } from '../utils';
import { OTPConfirmation } from './OTPConfirmation';
import { SuccessMessage } from './SuccessMessage';
export const EditPasswordDrawer = () => {
  const {
    t
  } = useTranslation('common');
  const {
    formState: {
      errors
    },
    clearErrors,
    handleSubmit,
    reset,
    setValue,
    setError,
    watch
  } = useForm<PasswordFormInputs>({
    defaultValues: {
      confirmPassword: '',
      password: ''
    },
    reValidateMode: 'onChange',
    mode: 'onChange'
  });
  const {
    data
  } = useGetAccount();
  const {
    mutate: sendOTP,
    isPending: isSendingOTP
  } = useSendOTP();
  const {
    mutate: updatePassword,
    isPending: isUpdatingPassword
  } = useUpdatePassword();
  const [isSuccess, setIsSuccess] = useState(false);
  const [isOTPExpired, setIsOTPExpired] = useState(false);
  const [hasOTPSendingError, setHasOTPSendingError] = useState(false);
  const [showOTPScreen, setShowOTPScreen] = useState(false);
  const password = watch('password');
  const confirmPassword = watch('confirmPassword');
  const handleOTPSend = () => sendOTP({
    identifier: data?.email ?? '',
    otp_type: 'email'
  }, {
    onError: () => setHasOTPSendingError(true),
    onSuccess: () => {
      setIsOTPExpired(false);
      setShowOTPScreen(true);
    }
  });
  const submit: SubmitHandler<PasswordFormInputs> = ({
    password,
    confirmPassword
  }) => {
    setHasOTPSendingError(false);
    if (password.length < 8) {
      setError('password', {
        message: t('drawers.personalInfo.updatePassword.password.helperText').replace('(', '').replace(')', '')
      });
    }
    if (password !== confirmPassword) {
      setError('confirmPassword', {
        message: t('drawers.personalInfo.updatePassword.confirmPassword.helperText').replace('(', '').replace(')', '')
      });
    }
    if (password.length >= 8 && password === confirmPassword) {
      handleOTPSend();
    }
  };
  const resetOnOTPExpiration = () => {
    reset();
    setIsOTPExpired(true);
  };
  const resetPasswordEdition = () => {
    reset();
    clearErrors();
    setIsSuccess(false);
    setShowOTPScreen(false);
  };
  return <Drawer id={EDIT_PROFILE_INFO_DRAWER_IDS.password} headerContent={isSuccess ? undefined : <Text size="2xl" color="content.primary">
            {t('drawers.personalInfo.updatePassword.header')}
          </Text>} closeButtonProps={{
    top: '3.125rem'
  }} headerProps={{
    pl: '3.4375rem',
    pr: '3rem',
    py: '2.8125rem'
  }} drawerProps={{
    closeOnEsc: !isSuccess,
    closeOnOverlayClick: !isSuccess
  }} showCloseButton={!isSuccess} handleBeforeDrawerClose={resetPasswordEdition} data-sentry-element="Drawer" data-sentry-component="EditPasswordDrawer" data-sentry-source-file="PasswordDrawer.tsx">
      <Flex flexDir="column" h="full" justify="space-between" py="l" data-sentry-element="Flex" data-sentry-source-file="PasswordDrawer.tsx">
        {isSuccess && <SuccessMessage resetFlow={resetPasswordEdition} />}
        {!isSuccess && <OTPConfirmation type="email" isNewValueSet={showOTPScreen} isOTPExpired={isOTPExpired} resendOTP={handleOTPSend} handleOTPExpiration={resetOnOTPExpiration} handleOTPValidation={token => {
        if (token.length === 6) {
          updatePassword({
            newPassword: password,
            token
          }, {
            onSuccess: () => setIsSuccess(true)
          });
        }
      }}>
            <Flex as="form" flexDir="column" gap="xl">
              <FormControl label={t('drawers.personalInfo.updatePassword.password.label')} helperText={t('drawers.personalInfo.updatePassword.password.helperText')} errorMessage={errors.password?.message} isRequired>
                <Input name="password" placeholder={t('drawers.personalInfo.updatePassword.password.placeholder')} type="password" onChange={e => setValue('password', e.target.value)} />
              </FormControl>
              <FormControl label={t('drawers.personalInfo.updatePassword.confirmPassword.label')} helperText={t('drawers.personalInfo.updatePassword.confirmPassword.helperText')} errorMessage={errors.confirmPassword?.message} isRequired>
                <Input placeholder={t('drawers.personalInfo.updatePassword.confirmPassword.placeholder')} type="password" onChange={e => setValue('confirmPassword', e.target.value)} />
              </FormControl>
              <Flex flexDir="column" gap="m" w="full">
                <Button isDisabled={password.length === 0 || confirmPassword.length === 0} isLoading={isUpdatingPassword || isSendingOTP} onClick={handleSubmit(submit)}>
                  {t('drawers.personalInfo.buttons.save')}
                </Button>
                <Button variant="outline">
                  {t('drawers.personalInfo.buttons.cancel')}
                </Button>
                {hasOTPSendingError && <Text size="sm" lineHeight="1rem" color="alert.error">
                    Erro ao enviar o código. Por favor, aguarde um momento e
                    tente novamente!
                  </Text>}
              </Flex>
            </Flex>
          </OTPConfirmation>}
      </Flex>
    </Drawer>;
};
type PasswordFormInputs = {
  password: string;
  confirmPassword: string;
};